import { useState } from 'react';
import { toast } from 'react-toastify';
import { personApi, type PersonByDocument } from '../services/personApi';
import { maskDocument, unmaskDocument } from '../components/pages/credit-analysis/utils/documentUtils';
import { PartyRole } from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

export interface AddPartyFormValue {
    role: PartyRole;
    personId: string;
    name: string;
    document: string;
    description: string;
}

export type Resolved =
    | { state: 'idle' }
    | { state: 'searching' }
    | { state: 'found'; person: PersonByDocument }
    | { state: 'notFound' }
    | { state: 'error' };

// Campos que têm componente próprio para exibir o erro. Um 422 com fieldName fora desta
// lista não tem onde aparecer — a UI mostra num bloco residual para nada sumir.
const FIELDS_WITH_OWN_ERROR = ['role', 'name', 'description'];

export const isCompleteDocument = (doc: string): boolean => {
    const len = unmaskDocument(doc).length;
    return len === 11 || len === 14;
};

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !FIELDS_WITH_OWN_ERROR.includes(e.fieldName));

interface UseLegalCasePartyFormArgs {
    onSubmit: (value: AddPartyFormValue) => Promise<boolean>;
}

// Estado e regras do formulário de adição de parte. Duas formas de identificar a parte:
// 1) busca por documento (CPF/CNPJ) → resolve o personId no serviço `person`; o nome vem
//    do cadastro, não se digita nem se persiste localmente.
// 2) contraparte externa (INPI, cartório, banco terceiro) → sem cadastro em `person`, o
//    nome é digitado à mão (personId nulo). É a regra anti-duplicação (ADR 001) na tela.
export default function useLegalCasePartyForm({ onSubmit }: UseLegalCasePartyFormArgs) {
    const [role, setRole] = useState<PartyRole>(PartyRole.DEBTOR);
    const [document, setDocument] = useState('');
    const [description, setDescription] = useState('');
    const [resolved, setResolved] = useState<Resolved>({ state: 'idle' });
    const [externalMode, setExternalMode] = useState(false);
    const [externalName, setExternalName] = useState('');

    const changeDocument = (raw: string) => {
        setDocument(maskDocument(raw));
        setResolved({ state: 'idle' });
    };

    const search = async () => {
        if (!isCompleteDocument(document)) return;
        setResolved({ state: 'searching' });
        try {
            const person = await personApi.findPersonByDocument(document);
            setResolved(person ? { state: 'found', person } : { state: 'notFound' });
        } catch {
            // Falha de rede: não trava em "searching" (senão o botão fica desabilitado e o
            // usuário não tenta de novo). Estado 'error' permite repetir.
            toast.error('Falha ao consultar Pessoas. Tente novamente.');
            setResolved({ state: 'error' });
        }
    };

    const enableExternalMode = () => {
        // Preserva o documento já digitado: quem buscou e não achou reaproveita o CPF/CNPJ
        // como documento local da contraparte externa, sem redigitar.
        setExternalMode(true);
        setResolved({ state: 'idle' });
    };

    const backToSearch = () => {
        setExternalMode(false);
        setExternalName('');
        setResolved({ state: 'idle' });
    };

    const buildValue = (): AddPartyFormValue | null => {
        if (externalMode) {
            // Contraparte externa guarda nome/documento locais (ADR 001). Documento é
            // opcional; envia normalizado quando há.
            return { role, personId: '', name: externalName, document: unmaskDocument(document), description };
        }
        if (resolved.state === 'found') {
            return { role, personId: resolved.person.uuid, name: '', document: '', description };
        }
        return null;
    };

    const submit = async () => {
        const value = buildValue();
        if (!value) return;
        await onSubmit(value);
    };

    const canSubmit = externalMode
        ? externalName.trim().length > 0
        : resolved.state === 'found';

    return {
        role, setRole,
        document, changeDocument,
        description, setDescription,
        resolved,
        externalMode, enableExternalMode, backToSearch,
        externalName, setExternalName,
        search, submit, canSubmit,
    };
}
